/**
 * Query telemetry — append-only JSONL log of MCP tool calls.
 *
 * Stored under .cai/.cache/queries.jsonl (gitignored).
 * Used by `cai stats`, `cai health`, and the severity-weighted drift score
 * to understand which context files Claude actually reads vs. which sit unused.
 *
 * Design: append-only, fire-and-forget, never blocks the MCP server response.
 * Auto-rotates at 10k entries to keep reads fast.
 *
 * Disabled when CAI_NO_TELEMETRY env var is set.
 */
import { appendFileSync, existsSync, mkdirSync, readFileSync, renameSync, statSync } from "node:fs";
import { dirname, join } from "node:path";

export interface QueryEntry {
  ts: number;
  tool: string;
  file?: string;
  section?: string;
  tokens?: number;
}

export interface FileAggregation {
  file: string;
  hits: number;
  tokens: number;
  lastSeen: number;
}

const ROTATE_AT_LINES = 10_000;
const CACHE_DIR = join(".cai", ".cache");
const LOG_FILE = "queries.jsonl";
const ROTATED_FILE = "queries.jsonl.old";

function isDisabled(): boolean {
  return Boolean(process.env.CAI_NO_TELEMETRY);
}

function logPath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, LOG_FILE);
}

/**
 * Append a query entry. Never throws — telemetry must not break the MCP server.
 * Rotates the log when it grows beyond ROTATE_AT_LINES.
 */
export function appendQuery(projectRoot: string, entry: Omit<QueryEntry, "ts">): void {
  if (isDisabled()) return;
  try {
    const path = logPath(projectRoot);
    mkdirSync(dirname(path), { recursive: true });

    const line = JSON.stringify({ ts: Date.now(), ...entry }) + "\n";
    appendFileSync(path, line, "utf8");

    // Cheap rotation check: only stat occasionally
    if (Math.random() < 0.01) {
      maybeRotate(path);
    }
  } catch {
    // swallow — telemetry failures must never affect the user
  }
}

function maybeRotate(path: string): void {
  try {
    const size = statSync(path).size;
    // ~120 bytes per entry average; rotate at ~1.2 MB
    if (size < ROTATE_AT_LINES * 120) return;

    const lines = readFileSync(path, "utf8").split("\n");
    if (lines.length < ROTATE_AT_LINES) return;

    renameSync(path, join(dirname(path), ROTATED_FILE));
  } catch {
    // ignore
  }
}

/**
 * Read all queries from the active log (not the rotated one).
 * Returns empty array if no log exists.
 */
export function readQueries(
  projectRoot: string,
  opts: { sinceMs?: number } = {}
): QueryEntry[] {
  const path = logPath(projectRoot);
  if (!existsSync(path)) return [];

  let raw: string;
  try {
    raw = readFileSync(path, "utf8");
  } catch {
    return [];
  }

  const cutoff = opts.sinceMs ?? 0;
  const out: QueryEntry[] = [];
  for (const line of raw.split("\n")) {
    if (!line) continue;
    try {
      const parsed = JSON.parse(line) as QueryEntry;
      if (parsed.ts >= cutoff) out.push(parsed);
    } catch {
      // skip malformed line
    }
  }
  return out;
}

/**
 * Aggregate queries by file: hit count, total tokens consumed, last seen timestamp.
 * Sorted by hits descending.
 */
export function aggregateByFile(queries: QueryEntry[]): FileAggregation[] {
  const map = new Map<string, FileAggregation>();
  for (const q of queries) {
    if (!q.file) continue;
    const existing = map.get(q.file);
    if (existing) {
      existing.hits++;
      existing.tokens += q.tokens ?? 0;
      if (q.ts > existing.lastSeen) existing.lastSeen = q.ts;
    } else {
      map.set(q.file, {
        file: q.file,
        hits: 1,
        tokens: q.tokens ?? 0,
        lastSeen: q.ts,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.hits - a.hits);
}

/**
 * Aggregate queries by tool name. Useful for understanding which MCP tools
 * Claude actually uses vs. which are dead weight.
 */
export function aggregateByTool(queries: QueryEntry[]): { tool: string; hits: number }[] {
  const map = new Map<string, number>();
  for (const q of queries) {
    map.set(q.tool, (map.get(q.tool) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([tool, hits]) => ({ tool, hits }))
    .sort((a, b) => b.hits - a.hits);
}

export interface SectionAggregation {
  section: string;
  file: string;
  hits: number;
  tokens: number;
  lastSeen: number;
}

/**
 * Aggregate queries by section (file#section key).
 * Only includes entries that have a section field.
 * Sorted by hits descending.
 */
export function aggregateBySection(queries: QueryEntry[]): SectionAggregation[] {
  const map = new Map<string, SectionAggregation>();
  for (const q of queries) {
    if (!q.section || !q.file) continue;
    const key = `${q.file}#${q.section}`;
    const existing = map.get(key);
    if (existing) {
      existing.hits++;
      existing.tokens += q.tokens ?? 0;
      if (q.ts > existing.lastSeen) existing.lastSeen = q.ts;
    } else {
      map.set(key, {
        section: q.section,
        file: q.file,
        hits: 1,
        tokens: q.tokens ?? 0,
        lastSeen: q.ts,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.hits - a.hits);
}
